import { Injectable, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms'; // Import necessary form classes
import { GlobalMessageService } from '../../global-services/global-message.service';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { Router } from '@angular/router';
import { MeService } from '../../global-services/me.service';
import { UserService } from '../../global-services/user.service';
import { CreateUserModel, UpdateUserModel, UserModel, UserOrgPermission, ConsentType } from '../../global-data-models/user.model'; // Import UserOrgPermission
import { v4 as uuidv4 } from 'uuid';
import { PermissionsService } from '../../global-services/permissions.service';
import { GlobalConsentFormComponent, ConsentData } from '../../global-components/global-consent-form/global-consent-form.component';

// Custom validator for matching passwords
export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password = control.get('password');
  const passwordRepeat = control.get('passwordRepeat');

  if (password && passwordRepeat && password.value && passwordRepeat.value) {
    return password.value === passwordRepeat.value ? null : { passwordMismatch: true };
  }
  return null;
};

export type CrudUserMode = 'create' | 'read' | 'update';
export type CrudUserRoleContext = 'me' | 'orgAdmin' | 'sysAdmin';

export interface CrudUserUiState {
  mode: CrudUserMode;
  roleContext: CrudUserRoleContext;
  form: FormGroup;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class CrudUserFacade {

  state$;
  public form: FormGroup;
  private stateSubject: BehaviorSubject<CrudUserUiState>;

  private consentForm: GlobalConsentFormComponent | undefined;

  constructor(
    private fb: FormBuilder,
    private messageService: GlobalMessageService,
    private loadingService: GlobalLoadingService,
    private userService: UserService,
    private meService: MeService,
    private permissionsService: PermissionsService,
    private router: Router,
  ) {
    // Create base form with initial mode-specific controls
    this.form = this.createBaseForm(undefined, 'create');
    this.stateSubject = new BehaviorSubject<CrudUserUiState>({ mode: 'create', roleContext: 'me', form: this.form });
    this.state$ = this.stateSubject.asObservable();
  }

  async begin(form: GlobalConsentFormComponent, mode: CrudUserMode = 'read', roleContext: CrudUserRoleContext = 'me', userId?: string, orgId?: string) {
    try {
      this.loadingService.show();

      this.consentForm = form;
      this.consentForm.formGroup.get("data")?.setValue(true);

      let userData: UserModel | undefined;
      if (mode === 'read' || mode === 'update') {
        userData = await this.getUserData(roleContext, userId, orgId);

        /* update the consent form while we're here */
        const marketingValue: boolean = !!(userData.consent?.find((v) => v.type === ConsentType.MARKETING)?.status);
        const analyticsValue: boolean = !!(userData.consent?.find((v) => v.type === ConsentType.ANALYTICS)?.status);

        this.consentForm.formGroup.get("marketing")?.setValue(marketingValue);
        this.consentForm.formGroup.get("analytics")?.setValue(analyticsValue);
      }

      // Create form with appropriate controls based on mode
      this.form = this.createBaseForm(userData, mode);

      switch (mode) {
        case 'create':
          this.configureFormForCreate(this.form);
          break;
        case 'read':
          this.configureFormForRead(this.form);
          break;
        case 'update':
          this.configureFormForUpdate(this.form, userData);
          break;
        default:
          console.error('Invalid mode provided to CrudUserFacade.begin:', mode);
          throw new Error('Invalid form mode specified.');
      }

      this.stateSubject.next({ mode, roleContext, form: this.form });
    } catch (error) {
      const errorMessage = (error as any).message || 'An unknown error occurred';
      this.messageService.showError('Error loading user data', errorMessage);
      // Reset to a default empty create form on error
      this.form = this.createBaseForm(undefined, 'create');
      this.configureFormForCreate(this.form);
      this.stateSubject.next({ mode: 'create', roleContext: 'me', form: this.form });
    } finally {
      this.loadingService.hide();
    }
  }

  async saveButtonPressed(mode: CrudUserMode = 'read', roleContext: CrudUserRoleContext = 'me', userId?: string, orgId?: string): Promise<boolean> {
    try {
      this.loadingService.show();

      if (mode === 'read') {
        throw new Error('Cannot save in read mode');
      }

      if (!this.markFormTouchedAndCheckValidity()) {
        throw new Error('Form not valid');
      }

      const userPayload = this.transformFormValuesToPayload(mode);

      if (mode === 'update') {
        await this.updateUserData(userPayload as UpdateUserModel, roleContext, userId, orgId);
        return true;
      }

      // Handle create case when implemented
      if (mode === 'create') {
        userPayload.consent = [];
        await this.createUser(userPayload as CreateUserModel, roleContext, orgId);
        return true;
      }

      throw new Error('Invalid mode for save operation');
    } catch (error) {
      // Error handling
      const errorObj = error as any;
      const message = errorObj.error?.message || errorObj.message || 'Unknown error during save';
      this.messageService.showError('Save Failed', message);
      return false;
    } finally {
      this.loadingService.hide();
    }
  }

  private async updateUserData(userPayload: UpdateUserModel, roleContext: CrudUserRoleContext = 'me', userId?: string, orgId?: string) {
    if (roleContext === 'me') {
      const me = await this.meService.refreshMe();

      if (!me) {
        throw new Error('User data not available.');
      }

      const userData = await this.userService.meUpdateUser(userPayload);
      return userData;
    }

    if (roleContext === 'orgAdmin') {
      if (!userId || !orgId) {
        throw new Error('User ID and org id is required');
      }
      const userData = await this.userService.orgAdminGetUserById(userId, orgId);
      return userData;
    }

    if (roleContext === 'sysAdmin') {
      if (!userId) {
        throw new Error('User ID is required');
      }
      const userData = await this.userService.adminGetUserById(userId);
      return userData;
    }

    throw new Error('Invalid role context');
  }

  private async getUserData(roleContext: CrudUserRoleContext = 'me', userId?: string, orgId?: string): Promise<UserModel> {
    if (roleContext === 'me') {
      const me = await this.meService.refreshMe();

      if (!me) throw new Error('User data not available.');

      const userData = await this.userService.meGetUser();
      return userData;
    }

    if (roleContext === 'orgAdmin') {
      const userData = await this.userService.orgAdminGetUserById(userId!, orgId!);
      return userData;
    }

    if (roleContext === 'sysAdmin') {
      const userData = await this.userService.adminGetUserById(userId!);
      return userData;
    }

    throw new Error('Invalid role context');
  }

  async deletePermission(permission: UserOrgPermission) {
    try {
      this.loadingService.show();
      await this.permissionsService.deleteMePermission(permission.id);
      if (this.stateSubject.getValue().roleContext === 'me') {
        await this.meService.refreshMe();
      }
      this.messageService.showSuccess('Permission Deleted', 'User permission has been deleted successfully.');
      return true;
    } catch (error) {
      const errorObj = error as any;
      const message = errorObj.error?.message || errorObj.message || 'Unknown error during save';
      this.messageService.showError('Delete Failed', message);
      return false;
    } finally {
      this.loadingService.hide();
    }
  }

  private async createUser(user: CreateUserModel, roleContext: CrudUserRoleContext, orgId?: string) {
    if (roleContext === 'orgAdmin') {
      if (!orgId) {
        throw new Error('Org ID is required for org admin user creation');
      }
      await this.userService.orgAdminCreateUser(user, orgId);
      this.messageService.showSuccess('User Created', 'User has been created successfully with operator permissions.');
      return;
    }

    if (roleContext === 'sysAdmin') {
      await this.userService.sysAdminCreateUser(user);
      this.messageService.showSuccess('User Created', 'User has been created successfully.');
      return;
    }

    throw new Error('Invalid role context for user creation');
  }

  private async updateUser(userId: string, user: UpdateUserModel) {
    try {
      this.loadingService.show();
      await this.userService.updateUser(userId, user);
      this.messageService.showSuccess('User Updated', 'User details have been updated successfully.');
      if (this.stateSubject.getValue().roleContext === 'me') {
        await this.meService.refreshMe();
      }
    } catch (error) {
      const errorObj = error as any;
      const message = errorObj.error?.message || errorObj.message || 'Unknown error during save';
      this.messageService.showError('Save Failed', message);
    } finally {
      this.loadingService.hide();
    }
  }

  private createBaseForm(userData?: UserModel, mode: CrudUserMode = 'read'): FormGroup {
    const currentPermissions = userData?.userOrgPermissions ?? [];

    /* TODO: Needs title added into this form as its different from signup form */
    const form = this.fb.group({
      id: [{ value: userData?.id || uuidv4(), disabled: true }],
      firstName: [userData?.firstName || '', Validators.required],
      lastName: [userData?.lastName || '', Validators.required],
      email: [userData?.email || '', [Validators.required, Validators.email]],
      permissionIds: [currentPermissions],
    });

    // Add password fields right away for create mode
    if (mode === 'create') {
      (form as FormGroup<any>).addControl('password', this.fb.control('', [Validators.required, Validators.minLength(6)]));
      (form as FormGroup<any>).addControl('passwordRepeat', this.fb.control<string>('', Validators.required));
      form.addValidators(passwordMatchValidator);
    }

    return form;
  }

  private configureConsentForm(enable: boolean) {
    if(!enable){
      this.consentForm!.formGroup.disable();
      return;
    }
    
    this.consentForm!.formGroup.enable();
    this.consentForm!.formGroup.get("data")?.disable();
  }

  private configureFormForCreate(form: FormGroup): void {
    // Password controls already added in createBaseForm when mode is 'create'
    form.get('email')?.enable();
    form.enable();
    form.get('id')?.disable();

    this.configureConsentForm(true);
  }

  private configureFormForRead(form: FormGroup): void {
    form.disable();
    this.configureConsentForm(false);
  }

  private configureFormForUpdate(form: FormGroup, userData?: UserModel): void {
    // Make sure to remove password fields if they somehow exist
    if (form.contains('password')) {
      form.removeControl('password');
    }
    if (form.contains('passwordRepeat')) {
      form.removeControl('passwordRepeat');
    }

    form.removeValidators(passwordMatchValidator);
    form.updateValueAndValidity();

    form.enable();
    
    this.configureConsentForm(true);

    form.get('id')?.disable();
    form.get('email')?.disable();
  }

  markFormTouchedAndCheckValidity(): boolean {
    this.form.markAllAsTouched();
    return this.form.valid;
  }

  private transformFormValuesToPayload(mode: CrudUserMode): CreateUserModel | UpdateUserModel {
    const rawValues = this.form.getRawValue();
    const payload: Partial<CreateUserModel | UpdateUserModel> = {
      firstName: rawValues.firstName,
      lastName: rawValues.lastName,
    };

    if (mode === 'read') {
      throw new Error('Cannot transform form values in read mode');
    }

    if (mode === 'create') {
      (payload as CreateUserModel).email = rawValues.email;
      (payload as CreateUserModel).password = rawValues.password;
      return payload as CreateUserModel;
    }

    /* if update mode, we just want to get the new data from our consent component */
    
    const consentData: ConsentData = this.consentForm!.getConsentData();

    payload.consent = [];

    /* always true here as it was agreed upon during sign-up and cannot be revoked
    without account deletion */
    payload.consent?.push({
      consentString: "Data Consent",
      dateTimeUTC: new Date(),
      status: true,
      type: ConsentType.DATA
    });

    payload.consent?.push({
      consentString: "Data Processor Consent",
      dateTimeUTC: new Date(),
      status: consentData.MARKETING,
      type: ConsentType.MARKETING
    });

    payload.consent?.push({
      consentString: "Analytics Consent",
      dateTimeUTC: new Date(),
      status: consentData.ANALYTICS,
      type: ConsentType.ANALYTICS
    });

    return payload as UpdateUserModel;
    
  }
}
